/**
 * SEO — Per-page <head> management via react-helmet-async.
 *
 * Provides unique title, description, canonical URL, Open Graph, Twitter Card,
 * and JSON-LD structured data for each public page. This is critical for search
 * engine crawlers to distinguish pages and display correct snippets.
 */

import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Haive';
const BASE_URL = 'https://haive.dev';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;

interface SEOProps {
  /** Page title — will be formatted as "Title | Haive" (omit site name) */
  title: string;
  /** Meta description — 150-160 chars for best SERP display */
  description: string;
  /** Canonical path, e.g. "/" or "/pricing" */
  path: string;
  /** Override the default OG image URL */
  ogImage?: string;
  /** Additional JSON-LD structured data to inject */
  jsonLd?: Record<string, unknown>;
}

export function SEO({ title, description, path, ogImage, jsonLd }: SEOProps) {
  const canonicalUrl = `${BASE_URL}${path === '/' ? '' : path}`;
  const fullTitle = path === '/' ? title : `${title} | ${SITE_NAME}`;
  const image = ogImage ?? DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={fullTitle} />

      {/* Page-specific structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
